import React, { memo } from "react";
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  formatCountdown,
  getBillTotal,
  PaymentSectionProps,
  QR_PAYMENT_COUNTDOWN_SECONDS,
} from "@rista/core/customerDisplay";
import { getPaymentModeLabel } from "@rista/core/helpers/paymentKey";

import { SuccessIcon } from "../../../assets";
import QRCodeRenderer from "../PaymentQrRenderer";
import { useCountdown, usePaymentDetails } from "../hooks";
import type { MoneyFormat } from "@rista/core/utils/currency/format";


export const PaymentSection = memo(function PaymentSection({
  sale,
  money,
  paymentDetails,
  isPaymentInProcess,
  isOrderSuccess = false,
}: PaymentSectionProps) {
  const { qrPayment } = usePaymentDetails(paymentDetails);
  const  qrAmount = qrPayment?.requestAmount || 0;
  // isOrderSuccess is the one that fires in practice — the closed sale is replaced by a fresh
  // empty cart before this re-renders. The sale check stays for the path where the display is
  // still holding the closed document, e.g. a saved order reopened from My Orders.
  const showSuccess = isOrderSuccess;
  const isPayment = !showSuccess && isPaymentInProcess;

  const billTotal = getBillTotal(sale);

  if (showSuccess) {
    return <SuccessView />;
  }
  if (isPayment && qrPayment?.qrData && qrPayment?.paymentStatus === "Requested" ) {
    return (
      <QRPaymentView
        qrData={qrPayment.qrData}
        mode={qrPayment.mode}
        amount={qrAmount}
        money={money}
      />
    );
  }

  if (isPayment) {
    return (
      <PaymentProgressView
        amount={billTotal}
        money={money}
      />
    );
  }

  return null;
});

const SuccessView = memo(function SuccessView() {
  return (
    <View style={styles.successContainer}>
      <SuccessIcon />

      <Text style={styles.successTitle}>
        Order successfully placed
      </Text>
    </View>
  );
});

const PaymentProgressView = memo(function PaymentProgressView({
  amount,
  money,
}: {
  amount: number;
  money: MoneyFormat;
}) {
  return (
    <View style={styles.paymentStatusScreen}>
      <ActivityIndicator
        size="large"
        color="#12141A"
      />

      <Text style={styles.paymentStatusText}>
        Payment of {money(amount)} in progress
      </Text>
    </View>
  );
});

const QRPaymentView = memo(function QRPaymentView({
  qrData,
  mode,
  amount,
  money,
}: {
  qrData: string;
  mode?: string;
  amount: number;
  money: MoneyFormat;
}) {
  // Keyed on the QR itself: a cart edit mid-payment re-renders this without
  // remounting it, and only a genuinely new code should restart the clock.
  const secondsRemaining = useCountdown(QR_PAYMENT_COUNTDOWN_SECONDS, qrData);
  const modeLabel = getPaymentModeLabel(mode);

  return (
    <View style={styles.paymentStatusScreen}>
      <Text style={styles.paymentStatusSubtitle}>
        Complete your payment of
      </Text>

      <Text style={styles.paymentStatusAmount}>
        {money(amount)}
      </Text>

      <Text style={styles.countdown}>
        {secondsRemaining > 0
          ? `Expires in ${formatCountdown(secondsRemaining)}`
          : "QR code expired"}
      </Text>

      <View style={styles.qrCard}>
        <QRCodeRenderer
          url={qrData}
          size={220}
        />
      </View>

      {!!modeLabel && (
        <Text style={styles.poweredBy}>
          Powered by {modeLabel}
        </Text>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  paymentStatusScreen: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
    backgroundColor: "#FFFFFF",
  },

  paymentStatusText: {
    color: "#12141A",
    fontSize: 26,
    fontWeight: "500",
    textAlign: "center",
    marginTop: 24,
  },

  paymentStatusSubtitle: {
    color: "#12141A",
    fontSize: 24,
    textAlign: "center",
  },

  paymentStatusAmount: {
    color: "#12141A",
    fontSize: 32,
    fontWeight: "700",
    textAlign: "center",
    marginTop: 4,
    marginBottom: 16,
  },

  countdown: {
    color: "#6A7085",
    fontSize: 20,
    fontWeight: "600",
    textAlign: "center",
    marginBottom: 20,
  },

  poweredBy: {
    color: "#6A7085",
    fontSize: 16,
    textAlign: "center",
    marginTop: 20,
  },

  qrCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E3E6ED",
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
  },

  successContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
    backgroundColor: "#FFFFFF",
  },

  successTitle: {
    color: "#12141A",
    fontSize: 36,
    fontWeight: "600",
    textAlign: "center",
  },
});

export default PaymentSection;